import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Rocket, Target, Orbit, Database, TrendingUp, Zap, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const HomePage = () => {
  const features = [
    {
      icon: Target,
      title: 'ML-Powered Predictions',
      description: 'Random Forest algorithm predicts asteroid mining profitability with high accuracy',
      color: 'from-cyan-400 to-blue-600',
    },
    {
      icon: Orbit,
      title: 'Orbit Simulation',
      description: 'Advanced Skyfield-based calculations for mission planning and delta-v requirements',
      color: 'from-purple-400 to-pink-600',
    },
    {
      icon: Database,
      title: 'Blockchain Ledger',
      description: 'Immutable mining operation records secured with Python blockchain technology',
      color: 'from-yellow-400 to-orange-600',
    },
  ];

  const stats = [
    { label: 'Prediction Accuracy', value: '89%', icon: TrendingUp },
    { label: 'Asteroids Analyzed', value: '500+', icon: Zap },
    { label: 'Blockchain Security', value: '100%', icon: Shield },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-block mb-6" data-testid="hero-icon">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-400 to-purple-600 flex items-center justify-center animate-pulse">
                <Rocket className="w-10 h-10 text-white" />
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6" data-testid="hero-title">
              <span className="gradient-text">
                Asteroid Mining Profitability Platform
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-3xl mx-auto" data-testid="hero-description">
              Harness the power of Machine Learning, Orbit Simulation, and Blockchain to identify the most
              profitable asteroids for mining operations in deep space.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-testid="hero-actions">
              <Link to="/predict">
                <Button
                  size="lg"
                  className="cosmic-button text-base"
                  data-testid="predict-button"
                >
                  Start Prediction
                </Button>
              </Link>
              <Link to="/orbit">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 text-base"
                  data-testid="simulate-button"
                >
                  Simulate Orbit
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4" data-testid="stats-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="glass-card p-6 text-center"
                  data-testid={`stat-${index}`}
                >
                  <Icon className="w-8 h-8 mx-auto mb-3 text-cyan-400" />
                  <div className="text-3xl font-bold gradient-text mb-2">{stat.value}</div>
                  <div className="text-gray-400 text-sm">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-3xl sm:text-4xl font-bold text-center mb-12 gradient-text"
            data-testid="features-title"
          >
            Platform Features
          </motion.h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="glass-card p-6 hover:scale-105 transition-transform duration-300"
                  data-testid={`feature-${index}`}
                >
                  <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{feature.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4" data-testid="how-it-works-section">
        <div className="max-w-4xl mx-auto glass-card p-8 md:p-12">
          <h2 className="text-3xl font-bold text-center mb-8 gradient-text" data-testid="how-it-works-title">
            How It Works
          </h2>
          
          <div className="space-y-6">
            <div className="flex gap-4" data-testid="step-1">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center font-bold">1</div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-white">Input Asteroid Data</h3>
                <p className="text-gray-400 text-sm">Enter asteroid parameters including diameter, distance, composition, and orbital characteristics</p>
              </div>
            </div>
            
            <div className="flex gap-4" data-testid="step-2">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center font-bold">2</div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-white">ML Analysis & Orbit Simulation</h3>
                <p className="text-gray-400 text-sm">Our Random Forest model analyzes profitability while Skyfield calculates mission parameters</p>
              </div>
            </div>
            
            <div className="flex gap-4" data-testid="step-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-orange-600 flex items-center justify-center font-bold">3</div>
              <div>
                <h3 className="text-lg font-semibold mb-2 text-white">Blockchain Recording</h3>
                <p className="text-gray-400 text-sm">Mining operations are recorded on our immutable blockchain ledger for transparency</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-12"
          >
            <h2 className="text-3xl font-bold mb-4 gradient-text" data-testid="cta-title">
              Ready to Explore the Cosmos?
            </h2>
            <p className="text-gray-300 mb-8" data-testid="cta-description">
              Start analyzing asteroids and discover profitable mining opportunities in deep space.
            </p>
            <Link to="/predict">
              <Button size="lg" className="cosmic-button" data-testid="cta-button">
                Launch Platform
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
